import { Check, ThumbsUp } from '@huspy/briks-icons';
import {
  Box, Button, Text, Flex
} from '@huspy/briks-web';
import { VStack } from '@huspy/styled-system/jsx';
import { Image } from '@mantine/core';
import { imagesPath } from '@shared/css.const';
import { useTranslation } from 'react-i18next';
import { BankApplication } from '@modules/opportunities/entities/bankApplication/bankApplication.entity';
import { CaseCreationSubmitApplicationsRoute } from '@modules/opportunities/presentation/routes';
import useGetEmailAuthLink from '@modules/clients/entities/client/query/useGetEmailAuthLink';
import useGetEmailPreferences from '@modules/clients/entities/client/query/useGetEmailPreferences';
import { EMAIL_CHANNEL_PROVIDER, EmailChannelProviderType } from './const';

// eslint-disable-next-line max-len
const baseTranslation = 'opportunity.caseSubmission.submitApplications.bankAndHuspySubmission.directToBankSlido.emailSourceSelection';

const RecommendedBadge = ({ isRecommended }: { isRecommended: boolean }) => {
  const { t } = useTranslation();
  if (!isRecommended) return null;

  return (
    <Flex align='center'>
      <ThumbsUp color='#818CF8' height='14' width='14' />
      <Text ml='1' color='indigo.400' size='md'>
        {t(`${baseTranslation}.sendByYourOwn.recommend`)}
      </Text>
    </Flex>
  );
};

const EmailOptionCard = ({
  isActive,
  onClick,
  isRecommended,
  children,
}: {
  isActive: boolean;
  onClick: () => void;
  isRecommended: boolean;
  children: React.ReactNode;
}) => {
  if (isActive) {
    return (
      <VStack
        p='4'
        bg='indigo.50'
        borderRadius='4'
        borderColor='indigo.400'
        borderWidth='3'
        cursor='pointer'
        onClick={ onClick }
        w='50%'
      >
        <Flex w='100%'>
          <RecommendedBadge isRecommended={ isRecommended } />
          <Flex
            justify='center'
            align='center'
            ml='auto'
            p='1'
            borderRadius='50%'
            bg='black'
            w='5'
            h='5'
          >
            <Check color='white' height='20px' width='20px' />
          </Flex>
        </Flex>
        {children}
      </VStack>
    );
  }

  return (
    <VStack
      p='4'
      bg='neutral.100'
      alignItems='center'
      borderWidth='3'
      borderColor='base.white'
      borderRadius='4'
      cursor='pointer'
      onClick={ onClick }
      w='50%'
    >
      <Flex w='100%'>
        <RecommendedBadge isRecommended={ isRecommended } />
        <Flex ml='auto'>
          <Box
            w='5'
            h='5'
            borderRadius='50%'
            borderWidth='1'
            borderColor='black'
          />
        </Flex>
      </Flex>
      {children}
    </VStack>
  );
};

export const EmailSourceSelection = ({
  bankApplication,
  emailChannelProvider,
  setEmailChannelProvider,
  accountExternalId,
}: {
  bankApplication: BankApplication;
  emailChannelProvider: EmailChannelProviderType;
  setEmailChannelProvider: (emailChannel: EmailChannelProviderType) => void;
  accountExternalId: string;
}) => {
  const { t } = useTranslation();
  const { pathname } = CaseCreationSubmitApplicationsRoute.useMatch();
  const { refetch, isLoading, isRefetching } = useGetEmailAuthLink(
    `${window.location.origin}${pathname}?bank_application_id=${bankApplication.bank_application_external_id}`,
    accountExternalId
  );
  const { data: getEmailAuthInfo } = useGetEmailPreferences(accountExternalId);

  return (
    <VStack h='100%' p='8'>
      <Text size='xl' fontWeight='semibold' mb='4'>
        {t(`${baseTranslation}.title`, { bankName: bankApplication.bank_details.name })}
      </Text>
      <Flex gap='4'>
        <EmailOptionCard
          isActive={ emailChannelProvider === EMAIL_CHANNEL_PROVIDER.THIRD_PARTY }
          onClick={ () => setEmailChannelProvider(EMAIL_CHANNEL_PROVIDER.THIRD_PARTY) }
          isRecommended
        >
          <Box w='80' h='80'>
            <Image src={ `${imagesPath}/mail_box_1.png` } w='100%' h='100%' />
          </Box>
          <Text textAlign='center' size='lg' fontWeight='semibold'>
            {t(`${baseTranslation}.sendByYourOwn.title`)}
          </Text>
          <Text textAlign='center' size='md' color='neutral.800'>
            {t(`${baseTranslation}.sendByYourOwn.subtitle`)}
          </Text>
          {getEmailAuthInfo?.isVerified ? (
            <Flex py='2' px='3' bg='indigo.100' borderRadius='3'>
              <Check color='#818CF8' height='20px' width='20px' />
              <Text ml='1' color='indigo.400' size='md'>
                {t(`${baseTranslation}.emailRegistered`)}
              </Text>
            </Flex>
          ) : (
            <Button
              size='md'
              w='100%'
              mt='4'
              onClick={ () => {
                refetch().then((res) => {
                  if (res.data?.url) {
                    window.location.href = res.data?.url;
                  }
                });
              } }
              loading={ isLoading || isRefetching }
              variant={ isLoading || isRefetching ? 'outline' : 'primary' }
            >
              {t(`${baseTranslation}.sendByYourOwn.getStartedButton`)}
            </Button>
          )}
        </EmailOptionCard>
        <EmailOptionCard
          isActive={ emailChannelProvider === EMAIL_CHANNEL_PROVIDER.SELF }
          onClick={ () =>
            setEmailChannelProvider(EMAIL_CHANNEL_PROVIDER.SELF) }
          isRecommended={ false }
        >
          <Box w='80' h='80'>
            <Image src={ `${imagesPath}/mail_box_2.png` } w='100%' h='100%' />
          </Box>
          <Text textAlign='center' size='lg' fontWeight='semibold'>
            {t(`${baseTranslation}.sendToYourself.title`)}
          </Text>
          <Text textAlign='center' size='md' color='neutral.800'>
            {t(`${baseTranslation}.sendToYourself.subtitle`)}
          </Text>
          <Text textAlign='center' size='sm' color='neutral.500' mt='4'>
            {t(`${baseTranslation}.sendToYourself.description`)}
          </Text>
        </EmailOptionCard>
      </Flex>
    </VStack>
  );
};
